import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ChartBarIcon, GiftIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "react-tooltip";
import { authService } from "../services/auth";
import { toggleSidebar } from "../redux/ui/uiSlice";

const VerticalDotsIcon = (
  <svg
    className="h-4 w-4 flex-shrink-0"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z"
    />
  </svg>
);

const MoreMenu = ({
  collapsed = false,
  showSidebarToggle = true,
  onClose,
  currentUser = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { sidebarCollapsed } = useSelector((state) => state.ui);
  const showTools = Boolean(currentUser?.features?.tools);
  const showPortfolio = Boolean(currentUser?.features?.portfolio);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = () => {
    authService.logout();
    navigate("/login/");
    setIsOpen(false);
    onClose?.();
  };

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center text-[13px] font-medium rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-theme-primary ${
          collapsed ? "justify-center px-2 py-2" : "px-2.5 py-2"
        } text-theme-text opacity-55 hover:opacity-90 hover:bg-black hover:bg-opacity-5`}
        aria-label="More options"
        data-tooltip-id={collapsed && !isOpen ? "more-tooltip" : undefined}
        data-tooltip-content={collapsed && !isOpen ? "More" : undefined}
      >
        {VerticalDotsIcon}
        {!collapsed && <span className="ml-2.5">More</span>}
      </button>
      {collapsed && !isOpen && (
        <Tooltip id="more-tooltip" place="right" className="z-50" />
      )}

      {isOpen && (
        <div
          ref={menuRef}
          className="absolute bottom-full left-0 z-50 mb-2 w-48 rounded-lg border border-theme-secondary bg-theme-main shadow-lg"
        >
          <div className="py-1">
            {showSidebarToggle && (
              <button
                type="button"
                onClick={handleToggleSidebar}
                className="w-full text-left px-4 py-2 text-sm text-theme-text hover:bg-black hover:bg-opacity-10 transition-colors"
              >
                <div className="flex items-center">
                  <svg
                    className="h-4 w-4 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    {sidebarCollapsed ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M11.25 4.5l7.5 7.5-7.5 7.5m-6-15l7.5 7.5-7.5 7.5"
                      />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5"
                      />
                    )}
                  </svg>
                  {sidebarCollapsed ? "Expand" : "Collapse"}
                </div>
              </button>
            )}
            {showTools && (
              <button
                type="button"
                onClick={() => {
                  navigate("/tools/");
                  setIsOpen(false);
                  onClose?.();
                }}
                className="w-full text-left px-4 py-2 text-sm text-theme-text hover:bg-black hover:bg-opacity-10 transition-colors"
              >
                <div className="flex items-center">
                  <WrenchScrewdriverIcon className="h-4 w-4 mr-2" />
                  Tools
                </div>
              </button>
            )}
            {showPortfolio && (
              <button
                type="button"
                onClick={() => {
                  navigate("/analytics/traffic/");
                  setIsOpen(false);
                  onClose?.();
                }}
                className="w-full text-left px-4 py-2 text-sm text-theme-text hover:bg-black hover:bg-opacity-10 transition-colors"
              >
                <div className="flex items-center">
                  <ChartBarIcon className="h-4 w-4 mr-2" />
                  Analytics
                </div>
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                navigate("/invites/");
                setIsOpen(false);
                onClose?.();
              }}
              className="w-full text-left px-4 py-2 text-sm text-theme-text hover:bg-black hover:bg-opacity-10 transition-colors"
            >
              <div className="flex items-center">
                <GiftIcon className="h-4 w-4 mr-2" />
                Invites
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                navigate("/api/playground/");
                setIsOpen(false);
                onClose?.();
              }}
              className="w-full text-left px-4 py-2 text-sm text-theme-text hover:bg-black hover:bg-opacity-10 transition-colors"
            >
              <div className="flex items-center">
                <svg
                  className="h-4 w-4 mr-2"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5"
                  />
                </svg>
                API
              </div>
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="w-full text-left px-4 py-2 text-sm text-theme-text hover:bg-black hover:bg-opacity-10 transition-colors"
            >
              <div className="flex items-center">
                <svg
                  className="h-4 w-4 mr-2"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
                  />
                </svg>
                Log Out
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MoreMenu;
